import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScenarioIssue } from './types';
import { lineOfOffset, locatePointer } from './jsonLocate';

interface Props {
  text: string;
  onChange: (text: string) => void;
  issues: ScenarioIssue[];
  parseError: string | null;
}

export const ScenarioJsonEditor: React.FC<Props> = ({ text, onChange, issues, parseError }) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const located = useMemo(
    () =>
      issues.map(issue => {
        const span = locatePointer(text, issue.pointer);
        return { issue, span, line: span ? lineOfOffset(text, span[0]) : null };
      }),
    [issues, text]
  );

  const jumpTo = (span: [number, number] | null) => {
    const textarea = textareaRef.current;
    if (!textarea || !span) return;
    textarea.focus();
    textarea.setSelectionRange(span[0], span[1]);
    const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 18;
    textarea.scrollTop = Math.max(0, (lineOfOffset(text, span[0]) - 3) * lineHeight);
  };

  return (
    <div className="space-y-2" data-testid="scenario-json-editor">
      <textarea
        ref={textareaRef}
        aria-label={t('agents.scenario.jsonEditor')}
        spellCheck={false}
        className={`block h-[560px] w-full rounded-lg border px-3 py-2 font-mono text-xs leading-[18px] bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
          parseError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
        }`}
        value={text}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key !== 'Tab') return;
          e.preventDefault();
          const target = e.currentTarget;
          const { selectionStart, selectionEnd } = target;
          onChange(`${text.slice(0, selectionStart)}  ${text.slice(selectionEnd)}`);
          requestAnimationFrame(() => target.setSelectionRange(selectionStart + 2, selectionStart + 2));
        }}
      />
      {parseError && (
        <p className="text-xs text-red-600 dark:text-red-400">
          {t('agents.scenario.jsonParseError')}: {parseError}
        </p>
      )}
      {located.length > 0 && (
        <ul className="space-y-1">
          {located.map(({ issue, span, line }, index) => (
            <li key={index}>
              <button
                type="button"
                className="text-left text-xs text-red-600 hover:underline dark:text-red-400"
                onClick={() => jumpTo(span)}
              >
                {line !== null ? `${t('agents.scenario.line')} ${line}` : issue.pointer || '/'} — {issue.message}
                <span className="ml-1 font-mono text-gray-500">{issue.pointer}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
